import { z } from 'zod';
import { AccountDeleted, DeleteAccountInput } from '../../account.js';
import { AuthTokens, ChangePasswordInput, EmailLoginInput, EmailSignupInput, ForgotPasswordInput, OtpStartInput, OtpStarted, OtpVerifyInput, PasswordReset, PasswordResetRequested, RefreshTokenInput, ResetOtpVerified, ResetPasswordInput, VerifyResetOtpInput, } from '../../auth.js';
export const authRoutes = {
    signupEmail: {
        method: 'POST',
        path: '/auth/signup/email',
        body: EmailSignupInput,
        response: AuthTokens,
    },
    loginEmail: {
        method: 'POST',
        path: '/auth/login/email',
        body: EmailLoginInput,
        response: AuthTokens,
    },
    /**
     * Trade a refresh token for a new pair. UNAUTHENTICATED, deliberately — the
     * caller is here precisely because their access token has expired, so
     * demanding a valid one would make the endpoint useless.
     *
     * The refresh token is SINGLE USE: a successful call returns a new one and
     * the presented one stops working. See `RefreshTokenStore`.
     */
    refresh: {
        method: 'POST',
        path: '/auth/refresh',
        body: RefreshTokenInput,
        response: AuthTokens,
    },
    /** Authenticated; invalidates nothing server-side today (stateless JWT). */
    changePassword: {
        method: 'POST',
        path: '/me/password',
        body: ChangePasswordInput,
        response: z.object({ changed: z.literal(true) }),
    },
    /**
     * Step one: email a 6-digit code. UNAUTHENTICATED by nature — the caller is
     * someone who cannot sign in — which is why these three sit under `/auth`
     * beside login rather than under `/me` beside `changePassword`.
     *
     * No `/api` prefix, because this API has none: every route it serves is
     * `/auth/…`, `/me/…`, `/admin/…`. Adding a global prefix for these three
     * would move every existing endpoint, which is the one thing this change is
     * not allowed to do. A gateway in front is free to mount the whole API under
     * `/api`.
     */
    forgotPassword: {
        method: 'POST',
        path: '/auth/forgot-password',
        body: ForgotPasswordInput,
        response: PasswordResetRequested,
    },
    /** Step two: spend the code for a reset token. Attempt-capped and single use. */
    verifyResetOtp: {
        method: 'POST',
        path: '/auth/verify-reset-otp',
        body: VerifyResetOtpInput,
        response: ResetOtpVerified,
    },
    /** Step three: spend the reset token for the new password. Single use. */
    resetPassword: {
        method: 'POST',
        path: '/auth/reset-password',
        body: ResetPasswordInput,
        response: PasswordReset,
    },
    otpStart: {
        method: 'POST',
        path: '/auth/otp/start',
        body: OtpStartInput,
        response: OtpStarted,
    },
    otpVerify: {
        method: 'POST',
        path: '/auth/otp/verify',
        body: OtpVerifyInput,
        response: AuthTokens,
    },
    /**
     * Close your own account (App Store Review Guideline 5.1.1(v)).
     *
     * POST rather than `DELETE /me/account`, for two reasons that outrank the
     * verb being the tidier one. It carries a body — the password that
     * re-authenticates the act — and a DELETE body is the one request shape
     * HTTP clients, proxies and Fastify's own content-type handling disagree
     * about; a re-auth field that silently arrives empty would turn the check
     * into a formality. And it reads beside `POST /me/password` and
     * `POST /admin/members/:userId/delete`, which is what the rest of this API
     * already does with an irreversible act on the caller's own account.
     *
     * Idempotent: calling it on an account that is already closed returns the
     * tombstone it already has rather than a 404, so a client retrying a request
     * whose response it never saw is not told the account never existed.
     */
    deleteAccount: {
        method: 'POST',
        path: '/me/account/delete',
        body: DeleteAccountInput,
        response: AccountDeleted,
    },
};
//# sourceMappingURL=routes.js.map